import React, { createContext, useCallback, useContext, useState } from "react"

/**
 * MediaPipContext (historical name) — typing-activation signal bus.
 *
 * Originally this provider implemented a Picture-in-Picture overlay that
 * captured the active media screen as a snapshot, navigated home, and floated
 * over `SidekickChat` so the user could type. That flow was replaced by
 * `TypingChatInputDock`, which renders an inline `ChatTextInput` on the
 * current screen instead of navigating away.
 *
 * All that remains is a tiny one-shot signal used to coordinate the Type
 * button (in `ButtonCluster` → `SidekickView`) with the dock:
 *
 *   ButtonCluster (Type) → SidekickView.handleModeChange("type")
 *     → triggerTypingMode()
 *       → shouldActivateTyping bumps to a fresh timestamp
 *         → TypingChatInputDock shows the input + autofocuses
 *           → acknowledgeTypingActivation() resets the flag
 *
 * The provider/hook keep their old names to avoid churn at every call site;
 * a future rename to `TypingActivationContext` would be purely cosmetic.
 */

interface MediaPipContextValue {
  /**
   * Monotonically-increasing timestamp that bumps whenever Type is pressed.
   * `TypingChatInputDock` watches this value and shows the inline input
   * (+ remounts to re-trigger autofocus) on every change. Resets to 0 once
   * the dock acknowledges the activation.
   */
  shouldActivateTyping: number
  /** Bump `shouldActivateTyping` to ask the dock to activate. */
  triggerTypingMode: () => void
  /** Called by the dock once it has handled the activation. */
  acknowledgeTypingActivation: () => void
}

const MediaPipContext = createContext<MediaPipContextValue | null>(null)

export function MediaPipProvider({ children }: { children: React.ReactNode }) {
  const [shouldActivateTyping, setShouldActivateTyping] = useState(0)

  const acknowledgeTypingActivation = useCallback(() => {
    setShouldActivateTyping(0)
  }, [])

  const triggerTypingMode = useCallback(() => {
    setShouldActivateTyping(Date.now())
  }, [])

  return (
    <MediaPipContext.Provider
      value={{
        shouldActivateTyping,
        triggerTypingMode,
        acknowledgeTypingActivation,
      }}
    >
      {children}
    </MediaPipContext.Provider>
  )
}

export function useMediaPip() {
  const context = useContext(MediaPipContext)
  if (!context) {
    throw new Error("useMediaPip must be used within a MediaPipProvider")
  }
  return context
}
